import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  DraftPurchaseOrderFreight,
  FreightOptionInstructionRow,
  FreightOptionRow,
  SuggestOrderFreightInput,
} from '@erp/rpc';

export type {
  DraftPurchaseOrderFreight,
  FreightOptionInstructionRow,
  FreightOptionRow,
};

export function useFreightOptions(params: {
  warehouseId?: string;
  includeArchived?: boolean;
  enabled?: boolean;
}) {
  return useQuery({
    ...orpc.procurement.freightOptions.list.queryOptions({
      input: {
        warehouseId: params.warehouseId,
        includeArchived: params.includeArchived ?? false,
      },
    }),
    enabled: params.enabled ?? true,
  });
}

export function useFreightCodes(warehouseId: string | null) {
  return useQuery({
    ...orpc.procurement.freightOptions.listCodes.queryOptions({
      input: { warehouseId: warehouseId ?? '' },
    }),
    enabled: Boolean(warehouseId),
  });
}

function useInvalidateFreightOptions() {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: orpc.procurement.freightOptions.key(),
    });
}

export function useCreateFreightOption() {
  const invalidate = useInvalidateFreightOptions();
  return useMutation(
    orpc.procurement.freightOptions.create.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

export function useUpdateFreightOption() {
  const invalidate = useInvalidateFreightOptions();
  return useMutation(
    orpc.procurement.freightOptions.update.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

/** The freight the branch's rules suggest for lines about to be released. */
export function useSuggestOrderFreight(input: SuggestOrderFreightInput | null) {
  return useQuery({
    ...orpc.procurement.freightOptions.suggestOrderFreight.queryOptions({
      input: input ?? {
        supplierId: '',
        warehouseId: '',
        orderValue: 0,
        lines: [],
      },
    }),
    enabled: input !== null,
  });
}

/** A freight option's saved shipping instructions, most used first. */
export function useFreightOptionInstructions(
  freightOptionId: string | null,
  includeArchived = false,
) {
  return useQuery({
    ...orpc.procurement.freightOptions.listInstructions.queryOptions({
      input: { freightOptionId: freightOptionId ?? '', includeArchived },
    }),
    enabled: freightOptionId !== null && freightOptionId !== '',
  });
}

export function useAddFreightOptionInstruction() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.freightOptions.addInstruction.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.procurement.freightOptions.key(),
        }),
    }),
  );
}

export function useUpdateFreightOptionInstruction() {
  const queryClient = useQueryClient();
  return useMutation(
    orpc.procurement.freightOptions.updateInstruction.mutationOptions({
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: orpc.procurement.freightOptions.key(),
        }),
    }),
  );
}

export function useDraftFreight(draftPurchaseOrderId: string | null) {
  return useQuery({
    ...orpc.procurement.freightOptions.getDraftFreight.queryOptions({
      input: { draftPurchaseOrderId: draftPurchaseOrderId ?? '' },
    }),
    enabled: Boolean(draftPurchaseOrderId),
  });
}

export function useSetDraftFreight() {
  const invalidate = useInvalidateFreightOptions();
  return useMutation(
    orpc.procurement.freightOptions.setDraftFreight.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}

export function useRefreshFreightOptions() {
  const invalidate = useInvalidateFreightOptions();
  return useMutation(
    orpc.procurement.freightOptions.refresh.mutationOptions({
      onSuccess: invalidate,
    }),
  );
}
